/* eslint-disable @next/next/no-img-element */
"use client";

import { useState, useEffect } from "react";
import { Popover, Transition } from "@headlessui/react";
import { User, createClientComponentClient } from "@supabase/auth-helpers-nextjs";
import apiClient from "@/libs/api";

// A button to show user some account actions
//  1. Billing: open a Stripe Customer Portal to manage their billing (cancel subscription, update payment method, etc.).
//     You have to manually activate the Customer Portal in your Stripe Dashboard (https://dashboard.stripe.com/test/settings/billing/portal)
//     This is only available if the customer has a customerId (they made a purchase previously)
//  2. Logout: sign out and go back to homepage
// See more at https://shipfa.st/docs/components/buttonAccount
const ButtonAccount = () => {
  const supabase = createClientComponentClient();
  const [isLoading, _] = useState<boolean>(false);
  const [user, setUser] = useState<User>(null);

  useEffect(() => {
    const getUser = async () => {
      const { data } = await supabase.auth.getUser();

      setUser(data.user);
    };

    getUser();
  }, [supabase]);

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error(error);
    }
    setUser(null);
    window.location.href = "/";
  };

  const handleDashboard = () => {
    window.location.href = "/dashboard";
  };

  const handleHistory = () => {
    window.location.href = "/dashboard/history";
  };

  // const handleDashboard = () => {
  //   window.location.href = "/dashboard";
  // };

  const handleBilling = async () => {
    window.location.href = "https://app.lemonsqueezy.com/my-orders";
  };

  return (
    <Popover className="relative z-10">
      {({ open }) => (
        <>
          <Popover.Button className="btn">
            {user?.user_metadata?.avatar_url ? (
              <img
                src={user?.user_metadata?.avatar_url}
                alt={"Profile picture"}
                className="w-6 h-6 rounded-full shrink-0"
                referrerPolicy="no-referrer"
                width={24}
                height={24}
              />
            ) : (
              <span className="w-8 h-8 bg-base-100 flex justify-center items-center rounded-full shrink-0 capitalize">
                {user?.email?.charAt(0)}
              </span>
            )}

            {user?.user_metadata?.name ||
              user?.email?.split("@")[0] ||
              "Account"}

            {isLoading ? (
              <span className="loading loading-spinner loading-xs"></span>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className={`w-5 h-5 duration-200 opacity-50 ${open ? "transform rotate-180 " : ""
                  }`}
              >
                <path
                  fillRule="evenodd"
                  d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
          >
            <Popover.Panel className="absolute z-10 mt-3 w-full sm:w-auto">
              <div className="overflow-hidden rounded-xl shadow-xl ring-1 ring-base-content ring-opacity-5 bg-base-100 p-1">
                <div className="space-y-0.5 text-sm">
                  <button
                    className="flex items-center gap-2 hover:bg-error/20 hover:text-error duration-200 py-1.5 px-4 w-full rounded-lg font-medium"
                    onClick={handleDashboard}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg"
                      x="0px" y="0px" width="20" height="20"
                      viewBox="0 0 32 32"
                      fill="currentColor"
                    >
                      <path d="M 16.105469 2.5351562 C 15.649335 2.5315682 15.193947 2.6430484 14.791016 2.8710938 C 12.204135 4.3291982 9.3582739 6.0810231 6.3964844 7.7871094 A 1.0001 1.0001 0 0 0 6.390625 7.7910156 C 4.934111 8.6442641 3.7537333 9.984391 3.5507812 11.599609 A 1.0001 1.0001 0 0 0 3.5507812 11.601562 C 3.070352 15.475925 3.2819546 19.358573 4.1835938 23.226562 A 1.0001 1.0001 0 0 0 4.1894531 23.246094 C 4.4585727 24.308167 5.1073155 25.27809 5.9121094 26.095703 C 6.7169032 26.913317 7.6791299 27.579013 8.7421875 27.863281 A 1.0001 1.0001 0 0 0 8.7617188 27.869141 C 13.579059 29.056186 18.420941 29.056186 23.238281 27.869141 A 1.0001 1.0001 0 0 0 23.257812 27.863281 C 24.320871 27.579013 25.283157 26.913542 26.087891 26.095703 C 26.892624 25.277864 27.542058 24.306963 27.810547 23.244141 A 1.0001 1.0001 0 0 0 27.814453 23.226562 C 28.718163 19.352345 28.930776 15.464439 28.447266 11.583984 A 1.0001 1.0001 0 0 0 28.445312 11.582031 C 28.242468 9.9798449 27.07737 8.6471954 25.634766 7.7910156 A 1.0001 1.0001 0 0 0 25.628906 7.7871094 C 22.732857 6.095988 19.951052 4.355459 17.417969 2.8886719 C 17.01807 2.6566693 16.561603 2.5387444 16.105469 2.5351562 z M 16.091797 4.5097656 C 16.190422 4.5107078 16.289962 4.5471445 16.414062 4.6191406 C 18.902981 6.0603535 21.691183 7.8027971 24.621094 9.5136719 C 25.63428 10.117407 26.372813 11.145633 26.460938 11.832031 C 26.91478 15.474383 26.718353 19.107322 25.869141 22.755859 C 25.727048 23.31585 25.268911 24.076675 24.662109 24.693359 C 24.055311 25.31004 23.303878 25.779371 22.744141 25.929688 C 21.997265 26.11325 21.253393 26.082382 20.507812 26.205078 C 20.945272 23.683317 20.958002 21.066257 20.421875 18.570312 A 1.0001 1.0001 0 0 0 20.396484 18.341797 A 1.0001 1.0001 0 0 0 20.375 18.261719 A 1.0001 1.0001 0 0 0 20.371094 18.246094 A 1.0001 1.0001 0 0 0 20.363281 18.222656 A 1.0001 1.0001 0 0 0 20.355469 18.203125 A 1.0001 1.0001 0 0 0 20.324219 18.130859 A 1.0001 1.0001 0 0 0 20.277344 18.042969 A 1.0001 1.0001 0 0 0 19.386719 17.550781 C 17.224714 16.610059 14.8144 16.617036 12.652344 17.554688 A 1.0001 1.0001 0 0 0 11.603516 18.341797 C 11.602316 18.346897 11.602764 18.352322 11.601562 18.357422 A 1.0001 1.0001 0 0 0 11.578125 18.564453 A 1.0001 1.0001 0 0 0 11.578125 18.568359 C 11.04162 21.064863 11.054622 23.68268 11.492188 26.205078 C 10.746607 26.082382 10.002735 26.113247 9.2558594 25.929688 C 8.6960893 25.779394 7.9448786 25.310016 7.3378906 24.693359 C 6.7320554 24.077874 6.2742554 23.319202 6.1308594 22.759766 C 5.2833441 19.117002 5.0844626 15.486039 5.5351562 11.849609 L 5.5351562 11.847656 C 5.623024 11.154921 6.3775335 10.115977 7.4023438 9.515625 C 10.394786 7.7916807 13.239164 6.0397011 15.773438 4.6113281 A 1.0001 1.0001 0 0 0 15.775391 4.6113281 C 15.895459 4.5433734 15.993171 4.5088235 16.091797 4.5097656 z M 16 18.744141 C 16.858834 18.742541 17.69746 19.001507 18.505859 19.339844 C 18.957083 21.671327 18.962039 24.134409 18.478516 26.455078 C 16.827372 26.604628 15.172628 26.604628 13.521484 26.455078 C 13.038759 24.138253 13.043261 21.679682 13.492188 19.351562 C 14.299845 19.009047 15.139857 18.745697 16 18.744141 z"></path>
                    </svg>
                    Dashboard
                  </button>

                  {/* New button for navigating to the history page */}
                  <button
                    className="flex items-center gap-2 hover:bg-base-300 duration-200 py-1.5 px-4 w-full rounded-lg font-medium"
                    onClick={handleHistory}
                  >
                    {/* Icon and Text for the History Button */}
                    <svg width="20" height="20" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                      <title>history-list</title>
                      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <g fill="currentColor" transform="translate(33.830111, 42.666667)">
                          <path d="M456.836556,405.333333 L456.836556,448 L350.169889,448 L350.169889,405.333333 L456.836556,405.333333 Z M328.836556,405.333333 L328.836556,448 L286.169889,448 L286.169889,405.333333 L328.836556,405.333333 Z M456.836556,341.333333 L456.836556,384 L350.169889,384 L350.169889,341.333333 L456.836556,341.333333 Z M328.836556,341.333333 L328.836556,384 L286.169889,384 L286.169889,341.333333 L328.836556,341.333333 Z M131.660221,261.176335 C154.823665,284.339779 186.823665,298.666667 222.169889,298.666667 C237.130689,298.666667 251.492003,296.099965 264.837506,291.382887 L264.838264,335.956148 C251.200633,339.466383 236.903286,341.333333 222.169889,341.333333 C175.041086,341.333333 132.37401,322.230407 101.489339,291.345231 L131.660221,261.176335 Z M456.836556,277.333333 L456.836556,320 L350.169889,320 L350.169889,277.333333 L456.836556,277.333333 Z M328.836556,277.333333 L328.836556,320 L286.169889,320 L286.169889,277.333333 L328.836556,277.333333 Z M222.169889,7.10542736e-15 C316.426487,7.10542736e-15 392.836556,76.4100694 392.836556,170.666667 C392.836556,201.752854 384.525389,230.897864 370.003903,256.000851 L317.574603,256.00279 C337.844458,233.356846 350.169889,203.451136 350.169889,170.666667 C350.169889,99.9742187 292.862337,42.6666667 222.169889,42.6666667 C151.477441,42.6666667 94.1698893,99.9742187 94.1698893,170.666667 C94.1698893,174.692297 94.3557269,178.674522 94.7192911,182.605232 L115.503223,161.830111 L145.673112,192 L72.836556,264.836556 L4.97379915e-14,192 L30.1698893,161.830111 L51.989071,183.641815 C51.6671112,179.358922 51.5032227,175.031933 51.5032227,170.666667 C51.5032227,76.4100694 127.913292,7.10542736e-15 222.169889,7.10542736e-15 Z M243.503223,64 L243.503223,159.146667 L297.903223,195.626667 L274.436556,231.04 L200.836556,182.186667 L200.836556,64 L243.503223,64 Z" />
                        </g>
                      </g>
                    </svg>
                    Docs History
                  </button>

                  <button
                    className="flex items-center gap-2 hover:bg-base-300 duration-200 py-1.5 px-4 w-full rounded-lg font-medium"
                    onClick={handleBilling}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M2.5 4A1.5 1.5 0 001 5.5V6h18v-.5A1.5 1.5 0 0017.5 4h-15zM19 8.5H1v6A1.5 1.5 0 002.5 16h15a1.5 1.5 0 001.5-1.5v-6zM3 13.25a.75.75 0 01.75-.75h1.5a.75.75 0 010 1.5h-1.5a.75.75 0 01-.75-.75zm4.75-.75a.75.75 0 000 1.5h3.5a.75.75 0 000-1.5h-3.5z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Billing
                  </button>
                  <button
                    className="flex items-center gap-2 hover:bg-error/20 hover:text-error duration-200 py-1.5 px-4 w-full rounded-lg font-medium"
                    onClick={handleSignOut}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="w-5 h-5"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3 4.25A2.25 2.25 0 015.25 2h5.5A2.25 2.25 0 0113 4.25v2a.75.75 0 01-1.5 0v-2a.75.75 0 00-.75-.75h-5.5a.75.75 0 00-.75.75v11.5c0 .414.336.75.75.75h5.5a.75.75 0 00.75-.75v-2a.75.75 0 011.5 0v2A2.25 2.25 0 0110.75 18h-5.5A2.25 2.25 0 013 15.75V4.25z"
                        clipRule="evenodd"
                      />
                      <path
                        fillRule="evenodd"
                        d="M6 10a.75.75 0 01.75-.75h9.546l-1.048-.943a.75.75 0 111.004-1.114l2.5 2.25a.75.75 0 010 1.114l-2.5 2.25a.75.75 0 11-1.004-1.114l1.048-.943H6.75A.75.75 0 016 10z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Logout
                  </button>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

export default ButtonAccount;
